import { useState, useEffect, useCallback } from 'react';
import { Lease } from '../../types/leases';
import { leasesApi } from '../../api/leasesApi';
import { LeaseMutations, useLeaseMutations } from './useLeaseMutations';

interface UseBuildingLeasesProps {
    buildingKey: string | undefined;
    /** Extra reload after a mutation, e.g. `fetchBuilding` so spots' currentLeaseKey refreshes. */
    onMutated?: () => Promise<void>;
}

export interface UseBuildingLeasesReturn extends LeaseMutations {
    leases: Lease[];
    loading: boolean;
    loadError: string | null;
    refresh: () => Promise<void>;
}

/** Leases of one building (indexed query). */
export const useBuildingLeases = ({ buildingKey, onMutated }: UseBuildingLeasesProps): UseBuildingLeasesReturn => {
    const [leases, setLeases] = useState<Lease[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!buildingKey) return;
        setLoading(true);
        setLoadError(null);
        try {
            setLeases(await leasesApi.getByBuilding(buildingKey));
        } catch (err) {
            console.error('Error loading leases:', err);
            setLoadError('Kunde inte ladda hyresavtal');
        } finally {
            setLoading(false);
        }
    }, [buildingKey]);

    useEffect(() => {
        if (buildingKey) load();
    }, [buildingKey, load]);

    const afterMutation = useCallback(async () => {
        await load();
        if (onMutated) await onMutated();
    }, [load, onMutated]);

    const mutations = useLeaseMutations(afterMutation);

    return { leases, loading, loadError, refresh: load, ...mutations };
};
